import React, { forwardRef, useState, useRef } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const Select = forwardRef(({
  label,
  error,
  options = [],
  placeholder,
  className = '',
  containerClassName = '',
  searchable = false,
  ...props
}, ref) => {
  const [search, setSearch] = useState('');
  const searchInputRef = useRef(null);
  const openRef = useRef(false);

  // Normalize value comparison (handle string vs number, etc.)
  const normalizeValue = (val) => {
    if (val === null || val === undefined || val === '') return '';
    return String(val);
  };

  const currentValue = props.value !== undefined && props.value !== null ? props.value : '';
  const selectedOption = options.find(opt => normalizeValue(opt.value) === normalizeValue(currentValue)) || null;
  const displayValue = selectedOption ? selectedOption.label : '';

  const filteredOptions = searchable && search
    ? options.filter(opt =>
      opt.label.toLowerCase().includes(search.toLowerCase()) ||
      (opt.value && String(opt.value).toLowerCase().includes(search.toLowerCase()))
    )
    : options;

  const handleChange = (selectedValue) => {
    setSearch('');

    if (!props.onChange) {
      return;
    }

    if (typeof props.onChange === 'function') {
      // react-hook-form register expects an event object with target.name and target.value
      // Create a synthetic event object for compatibility
      const fieldName = props.name || '';
      const event = {
        target: {
          value: selectedValue !== null && selectedValue !== undefined ? selectedValue : '',
          name: fieldName
        },
        type: 'change'
      };

      try {
        props.onChange(event);
      } catch (error) {
        console.error('Error calling onChange in Select:', error);
        // Fallback: try calling with just the value (for Controller compatibility)
        try {
          if (selectedValue !== null && selectedValue !== undefined) {
            props.onChange(selectedValue);
          }
        } catch (e) {
          console.error('Error calling onChange with value:', e);
        }
      }
    }
  };

  return (
    <div className={containerClassName}>
      {label && (
        <label className="label" htmlFor={props.id || props.name}>
          {label}
          {props.required && <span className="text-danger ml-1">*</span>}
        </label>
      )}

      <Listbox
        value={currentValue}
        onChange={handleChange}
        disabled={props.disabled}
      >
        {({ open }) => {
          // Focus search input when dropdown opens
          if (open && !openRef.current && searchable && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
          } else if (!open && openRef.current) {
            setSearch('');
          }
          openRef.current = open;

          return (
            <div className="relative">
              <Listbox.Button
                className={`
                  input
                  flex items-center justify-between w-full
                  text-left cursor-pointer
                  ${error ? 'input-error' : ''}
                  ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  ${className}
                `}
                ref={ref}
              >
                <span className={`block truncate ${!displayValue && !placeholder ? 'text-gray-400' : ''}`}>
                  {displayValue || placeholder || 'Select an option...'}
                </span>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''
                    }`}
                  aria-hidden="true"
                />
              </Listbox.Button>

              <Transition
                show={open}
                as={React.Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Listbox.Options
                  static
                  className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-auto focus:outline-none"
                >
                  {searchable && (
                    <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={search}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            e.stopPropagation();
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search..."
                          className="input pl-10 pr-4 w-full h-10 text-sm"
                          autoFocus
                        />
                      </div>
                    </div>
                  )}

                  {placeholder && (
                    <Listbox.Option
                      value=""
                      className={({ active, selected }) =>
                        `relative cursor-pointer select-none py-3 px-4 transition-colors ${selected ? 'bg-primary/10' : active ? 'bg-gray-50' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal text-gray-400'}`}>
                          {placeholder}
                        </span>
                      )}
                    </Listbox.Option>
                  )}

                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                      <Listbox.Option
                        key={option.value}
                        value={option.value}
                        className={({ active, selected }) =>
                          `relative cursor-pointer select-none py-3 px-4 transition-colors ${selected ? 'bg-primary/10' : active ? 'bg-gray-50' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <span className={`block truncate ${selected ? 'font-semibold text-primary' : 'font-normal'}`}>
                            {option.label}
                          </span>
                        )}
                      </Listbox.Option>
                    ))
                  ) : (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No options found
                    </div>
                  )}
                </Listbox.Options>
              </Transition>
            </div>
          );
        }}
      </Listbox>

      {error && (
        <p className="mt-1 text-sm text-danger">{error}</p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
